import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronRight, PanelLeft } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/cn.js';
import { Button } from './ui/button.js';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip.js';

/**
 * Sidebar do console.
 *
 * Duas larguras (expandida e só-ícones) e um drawer no celular. A escolha de colapsar
 * fica no localStorage: quem trabalha o dia inteiro no console decide uma vez.
 */

const KEY = 'talkium.sidebar';
const MOBILE_QUERY = '(max-width: 900px)';

interface SidebarApi {
  collapsed: boolean;
  toggle: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

const SidebarContext = createContext<SidebarApi>({
  collapsed: false,
  toggle: () => {},
  isMobile: false,
  mobileOpen: false,
  setMobileOpen: () => {},
});

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function matchesMobile(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_QUERY).matches
    : false;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [isMobile, setIsMobile] = useState(matchesMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    if (isMobile) {
      setMobileOpen((open) => !open);
      return;
    }
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(KEY, next ? 'collapsed' : 'expanded');
      } catch {
        /* sem armazenamento: vale só nesta visita */
      }
      return next;
    });
  }, [isMobile]);

  const api = useMemo<SidebarApi>(
    () => ({ collapsed: collapsed && !isMobile, toggle, isMobile, mobileOpen, setMobileOpen }),
    [collapsed, toggle, isMobile, mobileOpen],
  );

  return <SidebarContext.Provider value={api}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarApi {
  return useContext(SidebarContext);
}

export function SidebarTrigger({ className }: { className?: string }) {
  const { toggle, collapsed } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className={className}
      aria-label={collapsed ? 'Expandir navegação' : 'Recolher navegação'}
      onClick={toggle}
    >
      <PanelLeft />
    </Button>
  );
}

/**
 * A casca. No desktop é uma coluna fixa; no celular vira um drawer que fecha ao navegar.
 * `surface` troca a cor de destaque (o plano de operação usa `ops`).
 */
export function Sidebar({
  children,
  label,
  className,
}: {
  children: ReactNode;
  label: string;
  className?: string;
}) {
  const { collapsed, isMobile, mobileOpen, setMobileOpen } = useSidebar();

  const body = (
    <nav
      aria-label={label}
      className={cn(
        'flex h-full flex-col gap-1 overflow-y-auto overflow-x-hidden bg-sidebar px-2.5 py-3 text-sm',
        className,
      )}
    >
      {children}
    </nav>
  );

  if (isMobile) {
    return (
      <DialogPrimitive.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/55" />
          <DialogPrimitive.Content
            className="fixed inset-y-0 left-0 z-50 w-64 border-r border-sidebar-border bg-sidebar shadow-float outline-none"
            aria-describedby={undefined}
          >
            <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
            {body}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <aside
      data-collapsed={collapsed ? 'true' : 'false'}
      className={cn(
        'sticky top-0 h-screen shrink-0 border-r border-sidebar-border transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      {body}
    </aside>
  );
}

export function SidebarSection({ label, children }: { label?: string; children: ReactNode }) {
  const { collapsed } = useSidebar();
  return (
    <div className="flex flex-col gap-0.5">
      {label && !collapsed && (
        <div className="px-2.5 pt-3.5 pb-1 text-[11px] font-semibold tracking-wide text-subtle uppercase">
          {label}
        </div>
      )}
      {label && collapsed && <div className="mx-2 my-2 h-px bg-sidebar-border" aria-hidden />}
      {children}
    </div>
  );
}

export function SidebarItem({
  to,
  end,
  icon,
  label,
  badge,
  tag,
  onNavigate,
}: {
  to: string;
  end?: boolean;
  icon: ReactNode;
  label: string;
  /** Contador à direita (ex.: notificações não lidas). */
  badge?: ReactNode;
  /** Etiqueta curta à direita (ex.: "Operação"). */
  tag?: ReactNode;
  onNavigate?: () => void;
}) {
  const { collapsed, setMobileOpen } = useSidebar();

  const link = (
    <NavLink
      to={to}
      end={end ?? false}
      onClick={() => {
        setMobileOpen(false);
        onNavigate?.();
      }}
      className={({ isActive }) =>
        cn(
          'group flex h-9 items-center gap-2.5 rounded-md px-2.5 font-medium text-muted-foreground no-underline transition-colors hover:bg-muted hover:text-foreground hover:no-underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none [&_svg]:size-4 [&_svg]:shrink-0',
          isActive && 'bg-primary-soft text-primary hover:bg-primary-soft hover:text-primary',
          collapsed && 'justify-center px-0',
        )
      }
    >
      {icon}
      {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
      {!collapsed && badge}
      {!collapsed && tag}
      {collapsed && badge && (
        <span className="absolute top-1 right-1 size-2 rounded-full bg-danger" aria-hidden />
      )}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative">{link}</div>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/** Marca no topo da sidebar: monograma quando recolhida, nome quando expandida. */
export function SidebarBrand({
  name,
  mark = 'T',
  to = '/',
}: {
  name: string;
  mark?: string;
  to?: string;
}) {
  const { collapsed } = useSidebar();
  return (
    <NavLink
      to={to}
      className={cn(
        'mb-2 flex h-10 items-center gap-2.5 rounded-md px-2 text-[15px] font-semibold text-foreground no-underline hover:no-underline',
        collapsed && 'justify-center px-0',
      )}
    >
      <span
        aria-hidden="true"
        className="grid size-7 shrink-0 place-items-center rounded-md bg-primary text-xs font-bold text-primary-foreground"
      >
        {mark}
      </span>
      {!collapsed && <span className="truncate">{name}</span>}
    </NavLink>
  );
}

export { ChevronRight as SidebarChevron };
